import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { FloraRecord } from './models';
import { wgs84ToUtm } from './utm';

export interface ProjectDraft { name: string; responsible: string; description: string; }
export interface SiteDraft { project_id: string; name: string; type: string; description: string; }
export interface RecordDraft {
  project_id: string; site_id: string; common_name: string; scientific_name: string;
  responsible: string; activity: string; fecha: string; latitude: number | null;
  longitude: number | null; gps_accuracy: number | null; stratum: string;
  condition: string; individuals: number | null; coverage: number | null;
  height_m: number | null; dn_db_cm: number | null; notes: string;
}
export interface PendingPhoto { file: File; kind: string; photoId?: string; uploaded?: boolean; }

@Injectable({ providedIn: 'root' })
export class MonitoreoWriteService {
  private readonly backend = inject(SupabaseService);
  private async uid(): Promise<string> {
    const { data: { user }, error } = await this.backend.client.auth.getUser();
    if (error || !user) throw new Error('La sesión ha caducado. Vuelve a iniciar sesión.');
    return user.id;
  }
  async saveProject(draft: ProjectDraft, id?: string): Promise<void> {
    const uid = await this.uid();
    const name = draft.name.trim();
    if (!name) throw new Error('Introduce el nombre del proyecto.');
    const payload = { name, responsible: draft.responsible.trim() || null, description: draft.description.trim() || null };
    const query = id
      ? this.backend.client.from('proyectos').update(payload).eq('id', id).eq('owner_id', uid)
      : this.backend.client.from('proyectos').insert({ id: crypto.randomUUID(), owner_id: uid, active: true, ...payload });
    const { data, error } = await query.select('id').maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('No se guardó el proyecto. Revisa los permisos o vuelve a cargar.');
  }
  async saveSite(draft: SiteDraft, id?: string): Promise<void> {
    const uid = await this.uid();
    if (!draft.project_id || !draft.name.trim()) throw new Error('Selecciona proyecto y nombre del transecto.');
    const { data: owned, error: ownershipError } = await this.backend.client.from('proyectos')
      .select('id').eq('id', draft.project_id).eq('owner_id', uid).maybeSingle();
    if (ownershipError) throw ownershipError;
    if (!owned) throw new Error('No tienes acceso al proyecto seleccionado.');
    const payload = { project_id: draft.project_id, name: draft.name.trim(), type: draft.type.trim() || null, description: draft.description.trim() || null };
    const query = id
      ? this.backend.client.from('sitios_transectos').update(payload).eq('id', id).eq('project_id', draft.project_id)
      : this.backend.client.from('sitios_transectos').insert({ id: crypto.randomUUID(), ...payload });
    const { data, error } = await query.select('id').maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('No se guardó el sitio o transecto. Revisa los permisos.');
  }
  /** Guarda sin sustitución de ID. Si el sello updated_at cambió, exige revisión
   * antes de evitar sobrescribir una actualización previa de otro cliente. */
  async saveRecord(draft: RecordDraft, old?: FloraRecord, id?: string): Promise<string> {
    await this.uid();
    if (!draft.project_id || !draft.site_id || !draft.responsible.trim() || !draft.activity.trim() || !draft.fecha)
      throw new Error('Completa proyecto, transecto, responsable, actividad y fecha.');
    if (!draft.common_name.trim() && !draft.scientific_name.trim())
      throw new Error('Indica al menos un nombre de la especie.');
    const lat = Number(draft.latitude); const lon = Number(draft.longitude);
    if (draft.latitude === null || draft.longitude === null || !Number.isFinite(lat) || !Number.isFinite(lon) ||
      Math.abs(lat) > 90 || Math.abs(lon) > 180 || (lat === 0 && lon === 0))
      throw new Error('Captura coordenadas WGS84 válidas. No se admite 0, 0.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.fecha) || Number.isNaN(Date.parse(draft.fecha)))
      throw new Error('La fecha del monitoreo no es válida.');
    if (draft.gps_accuracy !== null && (!Number.isFinite(draft.gps_accuracy) || draft.gps_accuracy < 0))
      throw new Error('La precisión GPS debe ser un número positivo.');
    if (draft.individuals !== null && (!Number.isInteger(draft.individuals) || draft.individuals < 0))
      throw new Error('El número de individuos debe ser un entero mayor o igual a cero.');
    for (const [label, n, max] of [
      ['Cobertura', draft.coverage, 100], ['Altura', draft.height_m, 1000], ['Diámetro', draft.dn_db_cm, 100000]
    ] as const) {
      if (n !== null && (!Number.isFinite(n) || n < 0 || n > max)) throw new Error(`${label}: valor fuera del rango permitido.`);
    }
    const { data: site, error: siteError } = await this.backend.client.from('sitios_transectos')
      .select('id').eq('id', draft.site_id).eq('project_id', draft.project_id).maybeSingle();
    if (siteError) throw siteError;
    if (!site) throw new Error('El transecto seleccionado no pertenece al proyecto.');
    const utm = wgs84ToUtm(lat, lon);
    const payload = {
      project_id: draft.project_id, site_id: draft.site_id,
      common_name: draft.common_name.trim() || null, scientific_name: draft.scientific_name.trim() || null,
      responsible: draft.responsible.trim(), activity: draft.activity.trim(), fecha: draft.fecha,
      latitude: lat, longitude: lon, gps_accuracy: draft.gps_accuracy,
      datum: 'WGS84', utm_zone: utm?.zone ?? null, utm_hemisphere: utm?.hemisphere ?? null,
      utm_easting: utm?.easting ?? null, utm_northing: utm?.northing ?? null,
      stratum: draft.stratum.trim() || null, condition: draft.condition.trim() || null,
      individuals: draft.individuals, coverage: draft.coverage, height_m: draft.height_m,
      dn_db_cm: draft.dn_db_cm, notes: draft.notes.trim() || null,
      updated_at: new Date().toISOString()
    };
    if (old) {
      if (!id || id !== old.id) throw new Error('No se encontró el identificador original.');
      // Los UUID existentes siguen siendo los mismos: no duplicar un registro por edición.
      let query = this.backend.client.from('registros_flora').update(payload).eq('id', old.id);
      query = old.updated_at ? query.eq('updated_at', old.updated_at) : query.is('updated_at', null);
      const { data, error } = await query.select('id').maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Este registro cambió desde que abriste la ficha. Recarga antes de guardar para evitar sobrescribir datos.');
      return old.id;
    }
    const newId = id ?? crypto.randomUUID();
    const { data, error } = await this.backend.client.from('registros_flora')
      .insert({ id: newId, ...payload, created_at: new Date().toISOString() }).select('id').single();
    if (error) throw error;
    return String(data.id);
  }
  async addPhotos(recordId: string, projectId: string, incoming: PendingPhoto[], onSaved?: (file: File) => void): Promise<number> {
    const uid = await this.uid();
    let uploaded = 0;
    for (const { file, kind } of incoming) {
      const extension = file.type === 'image/png' ? 'png' : file.type === 'image/jpeg' ? 'jpg' : '';
      if (!extension || file.size <= 0 || file.size > 10 * 1024 * 1024)
        throw new Error(`La imagen ${file.name} debe ser JPG/PNG y pesar menos de 10 MB.`);
      const photoId = incoming.find(p => p.file === file)?.photoId ?? crypto.randomUUID();
      const pending = incoming.find(p => p.file === file);
      if (pending) pending.photoId = photoId;
      const path = `${uid}/${projectId}/${recordId}/${photoId}.${extension}`;
      if (!pending?.uploaded) {
        const { error: uploadError } = await this.backend.client.storage.from('flora-fotografias')
          .upload(path, file, { contentType: file.type, upsert: false });
        if (uploadError) throw new Error(`No se pudo subir ${file.name}: ${uploadError.message}`);
        if (pending) pending.uploaded = true;
      }
      const { error: rowError } = await this.backend.client.from('registro_fotos').insert({
        id: photoId, record_id: recordId, photo_type: kind || 'GENERAL', remote_path: path,
        created_at: new Date().toISOString()
      });
      if (rowError) {
        throw new Error(`La imagen ${file.name} se cargó en Storage pero su ficha no se registró: ${rowError.message}. Conserva el archivo para recuperación.`);
      }
      uploaded++;
      onSaved?.(file);
    }
    return uploaded;
  }
}
